import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type AssetType = 'script' | 'style';
export interface Asset { url: string; type: AssetType; }

interface LoadedAsset {
  element: HTMLScriptElement | HTMLLinkElement;
  count: number;
  type: AssetType;
}

@Injectable({ providedIn: 'root' })
export class AssetLoaderService {
  private loadedAssets = new Map<string, LoadedAsset>();
  private assetBundles = new Map<string, Asset[]>();

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /** Tekil varlık yükleme (async, tekrar yüklemez, sayaçlı) */
  loadOnce(url: string, type: AssetType): Promise<void> {
    if (this.loadedAssets.has(url)) {
      this.loadedAssets.get(url)!.count++;
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      let element: HTMLScriptElement | HTMLLinkElement;

      if (type === 'script') {
        element = this.document.createElement('script');
        (element as HTMLScriptElement).src = url;
        (element as HTMLScriptElement).async = true;
        (element as HTMLScriptElement).defer = true;
        element.type = 'text/javascript';
        element.onload = () => resolve();
        element.onerror = () => reject(new Error(`Script load failed: ${url}`));
        this.document.body.appendChild(element);
      } else {
        element = this.document.createElement('link');
        element.rel = 'stylesheet';
        element.type = 'text/css';
        (element as HTMLLinkElement).href = url;
        element.onload = () => resolve();
        element.onerror = () => reject(new Error(`Style load failed: ${url}`));
        this.document.head.appendChild(element);
      }

      this.loadedAssets.set(url, { element, count: 1, type });
    });
  }

  /** Tekil varlık kaldırma (sayaçlı, DOM'dan siler) */
  unload(url: string): void {
    const asset = this.loadedAssets.get(url);
    if (!asset) return;

    asset.count--;
    if (asset.count <= 0) {
      if (asset.type === 'script') {
        this.document.body.removeChild(asset.element);
      } else {
        this.document.head.removeChild(asset.element);
      }
      this.loadedAssets.delete(url);
    }
  }

  /** Yüklenmiş mi? */
  isLoaded(url: string): boolean {
    return this.loadedAssets.has(url);
  }

  /** Toplu asset yükleme (Promise.all) */
  loadBatch(assets: Asset[]): Promise<void[]> {
    return Promise.all(assets.map(a => this.loadOnce(a.url, a.type)));
  }

  /** Toplu asset kaldırma */
  unloadBatch(urls: string[]): void {
    urls.forEach(url => this.unload(url));
  }

  /** Asset bundle tanımlama */
  defineBundle(name: string, assets: Asset[]): void {
    this.assetBundles.set(name, assets);
  }

  /** Tanımlı bundle'ı yükle */
  loadBundle(name: string): Promise<void[]> {
    const assets = this.assetBundles.get(name);
    if (!assets) return Promise.reject(new Error(`Bundle "${name}" not found.`));
    return this.loadBatch(assets);
  }

  /** Tanımlı bundle'ı kaldır */
  unloadBundle(name: string): void {
    const assets = this.assetBundles.get(name);
    if (!assets) return;
    this.unloadBatch(assets.map(a => a.url));
  }

  /** Preload özelliği – dosyayı arka planda indir ama yükleme */
  preloadAsset(url: string, type: AssetType): void {
    const link = this.document.createElement('link');
    link.rel = 'preload';
    link.as = type === 'script' ? 'script' : 'style';
    link.href = url;
    link.crossOrigin = 'anonymous'; // Opsiyonel
    this.document.head.appendChild(link);
  }
}
